/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/Manual/EditManualWIFEndpointComponent" />
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionsCreatorInstanceIds } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { EndpointsEditorStore } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointsEditorStore";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import React = require("react");
import { WIFEndpointFooterComponent } from "./WIFEndpointFooterComponent";
import { EndpointNameInput } from "../Common/EndpointNameInput";
import { EndpointDescriptionInput } from "../Common/EndpointDescriptionInput";
import { EndpointErrorMessageWidget } from "../Common/EndpointErrorMessageWidget";
import { hasFederationSubjectChanged } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/ServiceEndpointExtensions";
import { Link } from "VSSUI/Link";
import { FederationDetails } from "../Common/FederationDetails";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { ARMAuthenticationDetailsHeader } from "./ARMAuthenticationDetailsHeader";
import { PipelinePermissionsCheckbox } from "../Common/PipelinePermissionsCheckbox";
import { CancelButton } from "../Common/BackButton";
import { EndpointsEditorActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/EndpointsEditorActionsCreator";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { InputDescriptor } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";
import { AzureEndpointConstants } from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { TestConnectionDataSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { EndpointSaveProgressStore, EndpointSaveProgressStoreEvents } from "DistributedTaskUI/ServiceEndpoints/Editor/Stores/EndpointSaveProgressStore";
import { NavigationActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Editor/Actions/NavigationActionsCreator";
import { SaveDraftEndpointButton } from "./SaveDraftEndpointButton";
import { VerifyAndSaveComponent } from "./VerifyAndSaveComponent";

interface IEditManualWIFEndpointComponentState {
    hasSubjectChanged: boolean;
    isEndpointValid: boolean;
    isDraftValid: boolean;
}

export class EditManualWIFEndpointComponent extends VssComponent<{}, IEditManualWIFEndpointComponentState> {

    private _invalidInputCount = 0;
    private _malformedInputs: string[] = [];

    private readonly _endpointEditorStore: EndpointsEditorStore;
    private readonly _validationScopeId = EditManualWIFEndpointComponent.name;
    private readonly _endpointEditorActionCreator: EndpointsEditorActionsCreator;
    private readonly _endpointSaveProgressStore: EndpointSaveProgressStore;
    private readonly _navigationActionsCreator: NavigationActionsCreator;

    constructor(context: IVssComponentContext) {
        super(context);

        this._navigationActionsCreator =  ActionCreatorManager.GetActionCreator<NavigationActionsCreator>(NavigationActionsCreator, ActionsCreatorInstanceIds.NavigationActionsCreatorInstanceId);

        this._endpointEditorActionCreator = ActionCreatorManager.GetActionCreator<EndpointsEditorActionsCreator>(EndpointsEditorActionsCreator, ActionsCreatorInstanceIds.EndpointsEditorActionsCreatorInstanceId);

        this._endpointSaveProgressStore = StoreManager.GetStore<EndpointSaveProgressStore>(EndpointSaveProgressStore, ActionsCreatorInstanceIds.EndpointSaveProgressStoreInstanceId);
        this._endpointSaveProgressStore.addListener(EndpointSaveProgressStoreEvents.Succeeded, this._onSaveSucceeded);

        this._endpointEditorStore = StoreManager.GetStore<EndpointsEditorStore>(EndpointsEditorStore, ActionsCreatorInstanceIds.EndpointsEditorStoreInstanceId);
        this._endpointEditorStore.addChangedListener(this._onStoreChange);

        const endpoint = this._endpointEditorStore.getEndpointClone();

        this.state = {
            hasSubjectChanged: hasFederationSubjectChanged(endpoint),
            isEndpointValid: true,
            isDraftValid: true
        };
    }

    public componentWillUnmount(): void {
        this._endpointEditorStore.removeChangedListener(this._onStoreChange);
        this._endpointSaveProgressStore.removeListener(EndpointSaveProgressStoreEvents.Succeeded, this._onSaveSucceeded);
    }

    public render(): JSX.Element {
        const type = this._endpointEditorStore.getState().serviceEndpointType;
        const endpoint = this._endpointEditorStore.getEndpointClone();
        return <React.Fragment>
            <div role="heading" aria-level={4} className="service-endpoint-step-header">{Resources.WorkloadIdentityFederationSectionBasics}</div>

            <EndpointNameInput validationScopeId={this._validationScopeId} />

            {this.state.hasSubjectChanged && this.renderNameChangeWarning()}

            <EndpointDescriptionInput validationScopeId={this._validationScopeId} />

            <div role="heading" aria-level={4} className="service-endpoint-step-header">App registration details</div>

            <FederationDetails />

            <GenericAuthorizationComponent
                disableAllPipelinesSetting={false}
                serviceEndpointType={type}
                draftEndpoint={endpoint}
                isUpdateForm={true}
                updateDraftEndpointState={this._updateEndpointState}
                updateInvalidInputsCount={this._updateInvalidInputsCount}
                onValidationChange={this._onValidationChange}
                renderAuthenticationHeader={() => <ARMAuthenticationDetailsHeader />}
            />

            <PipelinePermissionsCheckbox />

            <EndpointErrorMessageWidget />

            <WIFEndpointFooterComponent renderButtons={this._renderButtonSection} />
        </React.Fragment>;
    }

    private _updateInvalidInputsCount = (invalidInputCount: number) => {
        this._invalidInputCount += invalidInputCount;
        this.setState({
            isEndpointValid: this._invalidInputCount === 0
        });
    }

    private _onSaveSucceeded = (): void => {
        this._navigationActionsCreator.closePanel();
    }

    private _onValidationChange = (descriptor: InputDescriptor, isValid: boolean, isPresent: boolean): void => {
        if (!descriptor) {
            return;
        }

        const malformedInputs = new Set<string>(this._malformedInputs);
        if (!isValid && isPresent) {
            malformedInputs.add(descriptor.id);
        }
        else {
            malformedInputs.delete(descriptor.id);
        }
        this._malformedInputs = [...malformedInputs];

        this.setState({
            isDraftValid: this._malformedInputs.length === 0
        });
    }

    private _onStoreChange = (): void => {
        const endpoint = this._endpointEditorStore.getEndpointClone();
        this.setState({
            hasSubjectChanged: hasFederationSubjectChanged(endpoint)
        });
    }

    private renderNameChangeWarning(): JSX.Element {
        return (<React.Fragment>
            <div className="padding-top-4 padding-bottom-16">
                <span className="name-changes-warning-title">{Resources.Warning}:&nbsp;</span>
                <span className="name-changes-warning-description">{Resources.WorkloadIdentityFederationNameChangeWarningDescription}&nbsp;</span>
                <Link target="_blank" href="https://aka.ms/azdo-rm-workload-identity-guidance/">{Resources.LearnMoreText}</Link>
            </div>
        </React.Fragment>);
    }


    private _renderButtonSection = (): JSX.Element => {
        return (<React.Fragment>
            <CancelButton />
            {this.state.hasSubjectChanged && <SaveDraftEndpointButton closeUponSuccess={true} disabled={!this.state.isDraftValid} isUpdateFlow={true}/>}
            {!this.state.hasSubjectChanged && this.renderSaveButton()}
        </React.Fragment>);
    }

    private _updateEndpointState = (endpoint: ServiceEndpoint): void => {
        this._endpointEditorActionCreator.setServiceEndpoint(endpoint);
    }

    private _saveServiceEndpoint = (verifyBeforeSave: boolean) => {
        const openToAllPipelinesFlag = this._endpointEditorStore.getState().openToAllPipelinesFlag;
        const endpoint = this._endpointEditorStore.getEndpointClone();
        delete endpoint.data[AzureEndpointConstants.IsDraft];
        if (verifyBeforeSave) {
            this._endpointEditorActionCreator.verifyAndSaveServiceEndpoint(endpoint, TestConnectionDataSource, openToAllPipelinesFlag, true);
        }
        else {
            this._endpointEditorActionCreator.customSaveServiceEndpoint(endpoint, openToAllPipelinesFlag, true);
        }
    }

    private renderSaveButton(): JSX.Element {

        return (<VerifyAndSaveComponent
            disabled={!this.state.isEndpointValid}
            handleSaveClick={() => this._saveServiceEndpoint(false)}
            handleVerifyAndSave={() => this._saveServiceEndpoint(true)}
        />);

    }
}


